import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import HeroVariantA from "@/components/sections/heroes/HeroVariantA";
import HeroVariantB from "@/components/sections/heroes/HeroVariantB";
import HeroVariantC from "@/components/sections/heroes/HeroVariantC";

interface Variant {
  id: "A" | "B" | "C";
  name: string;
  summary: string;
  cta: string;
  Component: React.ComponentType;
}

const variants: Variant[] = [
  {
    id: "A",
    name: "Stage",
    summary:
      "Centered stack, huge H1, floating chips drift in the negative space the photo used to fill. Calm and breathing.",
    cta: "Primary + ghost (Explore Retreats + Watch the tour)",
    Component: HeroVariantA,
  },
  {
    id: "B",
    name: "Marquee",
    summary:
      "Centered H1, single primary CTA, then a live next-retreat pill with date + seats. Below the fold: rotated polaroid stack + Caveat quote.",
    cta: "Single primary (Explore Retreats) + live date pill",
    Component: HeroVariantB,
  },
  {
    id: "C",
    name: "Garden",
    summary:
      "Slightly smaller H1, an asymmetric constellation of chips and a hand-script 'join me!' arrow pointing at the CTA. Dual intent.",
    cta: "Dual primary (Join a Retreat + Shop Patterns)",
    Component: HeroVariantC,
  },
];

function VariantLabel({ v }: { v: Variant }) {
  return (
    <div
      className="sticky top-0 z-30 backdrop-blur-md px-6 py-3 border-b"
      style={{
        backgroundColor: `${colors.navy}f0`,
        borderColor: `${colors.tan}30`,
      }}
    >
      <div className="max-w-6xl mx-auto flex items-baseline gap-4">
        <span
          className="text-xs uppercase tracking-[0.25em] rounded px-2 py-1"
          style={{
            fontFamily: fonts.body,
            fontWeight: 800,
            backgroundColor: colors.berryRose,
            color: colors.cream,
          }}
        >
          Variant {v.id}
        </span>
        <h2
          className="text-2xl"
          style={{
            fontFamily: fonts.logo,
            color: colors.cream,
            fontWeight: 400,
          }}
        >
          {v.name}
        </h2>
        <p
          className="text-sm hidden md:block flex-1"
          style={{
            fontFamily: fonts.body,
            color: colors.cream,
            opacity: 0.75,
          }}
        >
          {v.summary}
        </p>
        <span
          className="text-xs hidden lg:inline"
          style={{
            fontFamily: fonts.body,
            color: colors.tan,
            fontWeight: 600,
          }}
        >
          {v.cta}
        </span>
      </div>
    </div>
  );
}

export function HeroIterations() {
  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>
      <div
        className="pt-24 pb-12 px-6 text-center"
        style={{ backgroundColor: colors.navy }}
      >
        <p
          className="text-xs uppercase mb-2"
          style={{
            fontFamily: fonts.body,
            fontWeight: 700,
            letterSpacing: "0.25em",
            color: colors.tan,
          }}
        >
          Design Iterations · Internal
        </p>
        <h1
          className="text-4xl sm:text-5xl mb-3"
          style={{
            fontFamily: fonts.logo,
            color: colors.cream,
            fontWeight: 400,
          }}
        >
          Hero redesign — 3 directions
        </h1>
        <p
          className="text-sm max-w-2xl mx-auto"
          style={{
            fontFamily: fonts.body,
            color: colors.cream,
            opacity: 0.7,
            letterSpacing: "0.03em",
          }}
        >
          Same DESIGN.md, three layout moods. Scroll through and tell me which
          direction to promote into HeroSection.tsx — or mix elements across.
        </p>
      </div>

      {variants.map((v) => (
        <div key={v.id}>
          <VariantLabel v={v} />
          <v.Component />
        </div>
      ))}

      <div
        className="py-16 px-6 text-center"
        style={{ backgroundColor: colors.navy }}
      >
        <p
          className="text-sm"
          style={{
            fontFamily: fonts.body,
            color: colors.cream,
            opacity: 0.6,
          }}
        >
          End of iterations.
        </p>
      </div>
    </div>
  );
}
